import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import apiService from '../services/apiService';

interface DashboardStats {
  salesStats: {
    totalRevenue: number;
    monthlyGrowth: number;
    ordersCount: number;
    quotationsCount: number;
    invoicesCount: number;
    recentOrders: Array<{
      id: string;
      orderNo: string;
      customerName: string;
      totalAmount: number;
      status: string;
      createdAt: string;
    }>;
  };
  crmStats: {
    totalCustomers: number;
    activeCustomers: number;
    conversionRate: number;
    pipelineValue: number;
    recentCustomers: Array<{
      id: string;
      name: string;
      email: string;
      createdAt: string;
    }>;
  };
  accountingStats: {
    totalRevenue: number;
    monthlyProfit: number;
    cashFlow: number;
  };
  inventoryStats: {
    totalProducts: number;
    lowStockItems: number;
    stockValue: number;
    warehouseCount: number;
    lowStockDetails: Array<{
      id: string;
      itemCode: string;
      itemName: string;
      currentStock: number;
      minStock: number;
      warehouses: string[];
    }>;
  };
  companyStats: {
    totalUsers: number;
    activeUsers: number;
    departments: number;
  };
  recentActivities: Array<{
    id: string;
    type: string;
    description: string;
    warehouse: string;
    timestamp: string;
    amount: string;
  }>;
  keyMetrics: {
    totalRevenue: number;
    monthlyGrowth: number;
    totalCustomers: number;
    totalProducts: number;
    lowStockAlerts: number;
    pendingOrders: number;
  };
  // Priority 4: Enhanced metadata
  dataSource: 'priority-3-apis' | 'legacy-apis' | 'mock-data';
  lastUpdated: string;
  priority3Status?: any;
}

interface RecentActivity {
  id: string;
  type: string;
  description: string;
  warehouse: string;
  timestamp: string;
  amount: string;
}

const DEFAULT_DASHBOARD_STATS: DashboardStats = {
  salesStats: {
    totalRevenue: 145750,
    monthlyGrowth: 12.5,
    ordersCount: 28,
    quotationsCount: 15,
    invoicesCount: 22,
    recentOrders: [
      { id: '1', orderNo: 'SO-000028', customerName: 'Tech Solutions Inc', totalAmount: 5200, status: 'confirmed', createdAt: '2024-06-20' },
      { id: '2', orderNo: 'SO-000027', customerName: 'Global Retail Corp', totalAmount: 3800, status: 'pending', createdAt: '2024-06-19' },
      { id: '3', orderNo: 'SO-000026', customerName: 'Demo Company Ltd', totalAmount: 1250, status: 'delivered', createdAt: '2024-06-18' }
    ]
  },
  crmStats: {
    totalCustomers: 156,
    activeCustomers: 89,
    conversionRate: 57.05,
    pipelineValue: 218625,
    recentCustomers: [
      { id: '1', name: 'Tech Solutions Inc', email: 'contact@techsolutions.com', createdAt: '2024-06-20' },
      { id: '2', name: 'Global Retail Corp', email: 'hello@globalretail.com', createdAt: '2024-06-15' }
    ]
  },
  accountingStats: {
    totalRevenue: 145750,
    monthlyProfit: 21862.5,
    cashFlow: 14575
  },
  inventoryStats: {
    totalProducts: 245,
    lowStockItems: 8,
    stockValue: 89340,
    warehouseCount: 3,
    lowStockDetails: [
      { id: '1', itemCode: 'LAPTOP-001', itemName: 'Business Laptop Pro', currentStock: 2, minStock: 10, warehouses: ['Main Warehouse'] },
      { id: '2', itemCode: 'MONITOR-001', itemName: '4K Monitor', currentStock: 1, minStock: 5, warehouses: ['Main Warehouse'] }
    ]
  },
  companyStats: {
    totalUsers: 12,
    activeUsers: 8,
    departments: 5
  },
  recentActivities: [
    { id: '1', type: 'sale', description: 'New sales order created', warehouse: 'Main Warehouse', timestamp: '2024-06-23T10:30:00Z', amount: '$5,200' },
    { id: '2', type: 'inventory', description: 'Stock level updated', warehouse: 'Main Warehouse', timestamp: '2024-06-23T09:15:00Z', amount: '15 units' },
    { id: '3', type: 'customer', description: 'New customer registered', warehouse: 'Main Warehouse', timestamp: '2024-06-23T08:45:00Z', amount: 'N/A' }
  ],
  keyMetrics: {
    totalRevenue: 145750,
    monthlyGrowth: 12.5,
    totalCustomers: 156,
    totalProducts: 245,
    lowStockAlerts: 8,
    pendingOrders: 28
  },
  dataSource: 'mock-data',
  lastUpdated: new Date().toISOString()
};

export const useDashboard = (companyId?: string) => {
  const [loading, setLoading] = useState(false);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>(DEFAULT_DASHBOARD_STATS);
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>(DEFAULT_DASHBOARD_STATS.recentActivities);
  const [error, setError] = useState<string | null>(null);

  // Priority 4: Enhanced data fetching with Priority 3 integration
  const fetchDashboardStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🚀 Priority 4: Fetching dashboard data with Priority 3 APIs...');
      
      // Try Priority 3 APIs first
      const response = await apiService.getDashboardStats(companyId) as any;
      
      console.log('📊 Dashboard API Response:', response);
      
      // Handle the enhanced response structure from Priority 4
      if (response && response.success && response.data) {
        const apiData = response.data;
        
        // Transform Priority 3 API data to dashboard format
        const transformedStats: DashboardStats = {
          salesStats: {
            totalRevenue: apiData.salesStats?.totalRevenue || 0,
            monthlyGrowth: apiData.salesStats?.monthlyGrowth || 0,
            ordersCount: apiData.salesStats?.totalOrders || 0,
            quotationsCount: apiData.salesStats?.totalQuotations || 0,
            invoicesCount: apiData.salesStats?.totalInvoices || 0,
            recentOrders: apiData.salesStats?.recentOrders || []
          },
          crmStats: {
            totalCustomers: apiData.crmStats?.totalCustomers || 0,
            activeCustomers: apiData.crmStats?.activeCustomers || 0,
            conversionRate: apiData.crmStats?.conversionRate || 0,
            pipelineValue: apiData.crmStats?.pipelineValue || 0,
            recentCustomers: apiData.crmStats?.recentCustomers || []
          },
          accountingStats: {
            totalRevenue: apiData.salesStats?.totalRevenue || 0,
            monthlyProfit: (apiData.salesStats?.totalRevenue || 0) * 0.15, // 15% margin
            cashFlow: (apiData.salesStats?.totalRevenue || 0) * 0.1 // 10% cash flow
          },
          inventoryStats: {
            totalProducts: apiData.inventoryStats?.length || 0,
            lowStockItems: Array.isArray(apiData.inventoryStats) ? apiData.inventoryStats.length : 0,
            stockValue: 0, // To be calculated
            warehouseCount: 1,
            lowStockDetails: Array.isArray(apiData.inventoryStats) ? apiData.inventoryStats : []
          },
          companyStats: {
            totalUsers: 12, // Default for now
            activeUsers: 8,
            departments: 5
          },
          recentActivities: DEFAULT_DASHBOARD_STATS.recentActivities, // Use default for now
          keyMetrics: {
            totalRevenue: apiData.salesStats?.totalRevenue || 0,
            monthlyGrowth: apiData.salesStats?.monthlyGrowth || 0,
            totalCustomers: apiData.crmStats?.totalCustomers || 0,
            totalProducts: apiData.inventoryStats?.length || 0,
            lowStockAlerts: Array.isArray(apiData.inventoryStats) ? apiData.inventoryStats.length : 0,
            pendingOrders: apiData.salesStats?.totalOrders || 0
          },
          dataSource: 'priority-3-apis',
          lastUpdated: new Date().toISOString(),
          priority3Status: apiData.priority3Status
        };
        
        setDashboardStats(transformedStats);
        console.log('✅ Priority 4: Successfully loaded data from Priority 3 APIs');
        
      } else {
        console.warn('⚠️ Priority 3 APIs returned unexpected format, using fallback data');
        setDashboardStats({
          ...DEFAULT_DASHBOARD_STATS,
          dataSource: 'legacy-apis',
          lastUpdated: new Date().toISOString()
        });
      }
      
    } catch (error) {
      console.error('❌ Priority 4: Error fetching dashboard stats:', error);
      setError('Failed to load dashboard data');
      
      // Use default data as fallback
      setDashboardStats({
        ...DEFAULT_DASHBOARD_STATS,
        dataSource: 'mock-data',
        lastUpdated: new Date().toISOString()
      });
      
      // Show user-friendly error message
      message.warning('Using demo data - backend connection unavailable');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  // Refresh function for manual data reload
  const refreshDashboard = useCallback(async () => {
    console.log('🔄 Priority 4: Manual dashboard refresh triggered');
    await fetchDashboardStats();
    message.success('Dashboard data refreshed successfully');
  }, [fetchDashboardStats]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    fetchDashboardStats();
    
    const interval = setInterval(() => {
      console.log('⏰ Priority 4: Auto-refreshing dashboard data...');
      fetchDashboardStats();
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(interval);
  }, [fetchDashboardStats]);

  // Priority 4: Enhanced helper functions with Priority 3 data
  const getQuickActions = useCallback(() => [
    { 
      title: 'New Sales Order', 
      icon: 'ShoppingCart', 
      path: '/sales/orders', 
      color: '#1890ff',
      description: 'Create a new sales order using Priority 3 APIs'
    },
    { 
      title: 'Add Lead', 
      icon: 'Contacts', 
      path: '/crm/leads', 
      color: '#52c41a',
      description: 'Add new lead to CRM system'
    },
    { 
      title: 'Create Invoice', 
      icon: 'Calculator', 
      path: '/sales/invoice-builder', 
      color: '#fa8c16',
      description: 'Generate invoice from sales data'
    },
    { 
      title: 'Add Product', 
      icon: 'Package', 
      path: '/inventory/products', 
      color: '#722ed1',
      description: 'Add product with Priority 3 APIs'
    },
    { 
      title: 'Purchase Order', 
      icon: 'Shop', 
      path: '/purchase/orders', 
      color: '#eb2f96',
      description: 'Create purchase order'
    },
    { 
      title: 'Add Employee', 
      icon: 'Team', 
      path: '/hr/employees', 
      color: '#13c2c2',
      description: 'Add new employee to HR system'
    }
  ], []);

  const getTopPerformers = useCallback(() => {
    if (!dashboardStats?.salesStats.recentOrders) return [];
    
    // Create performers based on Priority 3 sales data
    const customerFrequency: Record<string, number> = {};
    dashboardStats.salesStats.recentOrders.forEach(order => {
      customerFrequency[order.customerName] = (customerFrequency[order.customerName] || 0) + Number(order.totalAmount);
    });
    
    return Object.entries(customerFrequency)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 4)
      .map(([customer, revenue], index) => ({
        name: customer,
        role: 'Customer',
        performance: Math.min(95 - index * 5, 95),
        avatar: customer.split(' ').map(n => n[0]).join('').toUpperCase(),
        revenue,
        dataSource: dashboardStats.dataSource
      }));
  }, [dashboardStats?.salesStats.recentOrders, dashboardStats.dataSource]);

  // Priority 4: Enhanced metrics with real-time status
  const getEnhancedMetrics = useCallback(() => {
    return {
      apiStatus: dashboardStats.priority3Status ? 'connected' : 'disconnected',
      dataFreshness: dashboardStats.lastUpdated,
      dataSource: dashboardStats.dataSource,
      totalEndpoints: dashboardStats.priority3Status?.apiEndpoints ? 
        Object.keys(dashboardStats.priority3Status.apiEndpoints).length : 0,
      backendHealth: dashboardStats.priority3Status?.status || 'unknown'
    };
  }, [dashboardStats]);

  return {
    loading,
    error,
    salesStats: dashboardStats.salesStats,
    crmStats: dashboardStats.crmStats,
    accountingStats: dashboardStats.accountingStats,
    inventoryStats: dashboardStats.inventoryStats,
    companyStats: dashboardStats.companyStats,
    recentActivities,
    keyMetrics: dashboardStats.keyMetrics,
    refreshDashboard,
    quickActions: getQuickActions(),
    topPerformers: getTopPerformers(),
    
    // Priority 4: Enhanced features
    dashboardStats, // Full stats object
    enhancedMetrics: getEnhancedMetrics(),
    isRealTime: dashboardStats.dataSource === 'priority-3-apis',
    lastUpdated: dashboardStats.lastUpdated,
    dataSource: dashboardStats.dataSource,
    priority3Status: dashboardStats.priority3Status
  };
}; 